/* eslint-disable no-unused-vars */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { revenueReportsAPI } from 'services/reports.service';
import { publisherReportsRequestTypes, reportUniqueFieldsList } from 'constants';
import { useSignal, useComputed, useSignalEffect } from '@preact/signals-react';
import { downloadCSV } from './exportCSV.js';
import { publisherReportsFiltersTableActions } from 'store/actions';
import { reportTableColsOrder } from 'constants';

// ratio metrics (ctr, eCPM) must not be summed — they are recomputed from the summed totals
const colsToAdd = ['clicks', 'earning', 'rawEarning', 'impressions', 'installs'];

function parseDate(str) {
  // if (!str) return '';
  const [day, month, year] = str.split('-');
  return new Date(`${year}-${month}-${day}`);
}

const getCtr = (clicks, impressions) => {
  const res1 = +((clicks / impressions) * 100).toFixed(2);
  return Number.isNaN(res1) || impressions === 0 ? 0 : res1;
};

// earning must be the unrounded value: rounding it to cents first skews eCPM by up to 5/impressions
const getEcpm = (earning, impressions) => {
  const res1 = +((earning / impressions) * 1000).toFixed(3);
  return Number.isNaN(res1) || impressions === 0 ? 0 : res1;
};

const TABLE_ROW_INITIAL_COUNT = 5;
const TABLE_ROW_INCREMENT = 50;

const checkFiltersBeforeAdding = ({
  row,
  countriesFilterSet,
  placementsFilterSet,
  advertisersFilterSet,
}) => {
  const filters = [
    { field: 'country_code', filterSet: countriesFilterSet },
    { field: 'placement_id', filterSet: placementsFilterSet },
    { field: 'adveriser_id', filterSet: advertisersFilterSet },
  ];
  const res = filters.every(({ field, filterSet }) => {
    if (field in row && filterSet.size) {
      return filterSet.has(row[field]);
    }
    return true;
  });
  return res;
};

const formatDate = (dateString) => {
  return dateString?.split('/').reverse().join('.') || '';
};

export const useFacade = ({ selectedAppsNumbers$, currentRequestType$ }) => {
  const showAppName = useSelector((state) => state.auth.show_app_name);
  const dispatch = useDispatch();
  const periodFrom = useSelector((state) => state.reports.selectedDateRangeReports.from);
  const periodTo = useSelector((state) => state.reports.selectedDateRangeReports.to);

  const tableGrouping = useSelector((state) => state.publisherReportsFiltersTable.tableGrouping);
  const tableSorting = useSelector((state) => state.publisherReportsFiltersTable.tableSorting);

  const isSamsungUser = useSelector((state) => state.auth.isSamsungUser);

  const advertisersFilter = useSelector(
    (state) => state.publisherReportsFiltersTable.advertisersFilter,
  );
  const advertisersFilterSet$ = useSignal(new Set(advertisersFilter));

  useEffect(() => {
    advertisersFilterSet$.value = new Set(advertisersFilter);
  }, [advertisersFilter]);

  const countriesFilter = useSelector(
    (state) => state.publisherReportsFiltersTable.countriesFilter,
  );

  const placementsFilter = useSelector(
    (state) => state.publisherReportsFiltersTable.placementsFilter,
  );
  const placementsFilterSet$ = useSignal(new Set(placementsFilter));

  useEffect(() => {
    placementsFilterSet$.value = new Set(placementsFilter);
  }, [placementsFilter]);

  const countriesFilterSet$ = useSignal(new Set(countriesFilter));

  useEffect(() => {
    countriesFilterSet$.value = new Set(countriesFilter);
  }, [countriesFilter]);

  const { data, isLoading, isFetching, isError } = revenueReportsAPI.useGetReportQuery({
    to: periodTo,
    from: periodFrom,
    type: publisherReportsRequestTypes[currentRequestType$.value],
  });

  const uniqueField$ = useComputed(() => {
    return reportUniqueFieldsList[currentRequestType$.value];
  });

  const uniqueSubElemField$ = useComputed(() => {
    switch (currentRequestType$.value) {
      case 'applications':
        return 'placement_id';
      case 'country':
        return 'placement_id';
      case 'placement':
        return 'country_code';
      default:
        return '';
    }
  });

  const applicationsMap$ = useSignal(new Map());
  const placementList$ = useSignal(new Map());
  const countryList$ = useSignal(new Map());
  const advertiserList$ = useSignal(new Map());
  const originalTableData$ = useSignal([]);
  const sortConfig$ = useSignal(tableSorting); // 'ascending', 'descending' or null

  useSignalEffect(() => {
    dispatch(
      publisherReportsFiltersTableActions.updateProperty({
        propField: 'tableSorting',
        newValue: sortConfig$.value,
      }),
    );
  });

  const sortedFilteredData$ = useComputed(() => {
    countryList$.value.clear();
    placementList$.value.clear();
    advertiserList$.value.clear();

    const resultMap = new Map();
    const arrayIsEmpty = !selectedAppsNumbers$.value.length;
    const sortConfig = sortConfig$.value;

    // filter and remove duplicates in one pass
    originalTableData$.value?.forEach((row) => {
      const currentElem = structuredClone(row);

      currentElem.rawEarning = currentElem.earning;
      currentElem.earning = +currentElem.earning.toFixed(2);
      currentElem.ctr1 = getCtr(currentElem.clicks, currentElem.impressions);
      currentElem.eCPM1 = getEcpm(currentElem.rawEarning, currentElem.impressions);
      currentElem.subElements = [];

      const uniqueKey = row[uniqueField$.peek()];
      const isActive = selectedAppsNumbers$.value.includes(row.pba_number);

      // create lists of filter items
      applicationsMap$.value.set(row.pba_number, { ...row });

      if ('adveriser_id' in row && 'advertiser' in row) {
        if (!advertiserList$.value.has(row.adveriser_id)) {
          const advertiserItem = {
            adveriser_id: row.adveriser_id,
            advertiser: row.advertiser,
            pba_numbers: new Set([row.pba_number]),
          };
          advertiserList$.value.set(row.adveriser_id, advertiserItem);
        } else {
          const containedElem = { ...advertiserList$.value.get(row.adveriser_id) };
          containedElem.pba_numbers.add(row.pba_number);
          advertiserList$.value.set(row.adveriser_id, containedElem);
        }
      }
      if ('country' in row && 'country_code' in row) {
        if (!countryList$.value.has(row.country_code)) {
          const countryItem = {
            country_code: row.country_code,
            country: row.country,
            pba_numbers: new Set([row.pba_number]),
          };
          countryList$.value.set(row.country_code, countryItem);
        } else {
          const containedElem = { ...countryList$.value.get(row.country_code) };
          containedElem.pba_numbers.add(row.pba_number);
          countryList$.value.set(row.country_code, containedElem);
        }
      }
      if ('placement' in row && 'placement_id' in row) {
        if (!placementList$.value.has(row.placement_id)) {
          const placementItem = {
            placement: `${row.placement} (${showAppName ? row.apps : row.pba_number})`,
            placement_id: row.placement_id,
            pba_numbers: new Set([row.pba_number]),
          };
          placementList$.value.set(row.placement_id, placementItem);
        } else {
          const containedElem = { ...placementList$.value.get(row.placement_id) };
          containedElem.pba_numbers.add(row.pba_number);
          placementList$.value.set(row.placement_id, containedElem);
        }
      }

      if (arrayIsEmpty || isActive) {
        if (
          checkFiltersBeforeAdding({
            row,
            countriesFilterSet: countriesFilterSet$.value,
            placementsFilterSet: placementsFilterSet$.value,
            advertisersFilterSet: advertisersFilterSet$.value,
          })
        ) {
          if (!resultMap.has(uniqueKey)) {
            const elemToPush = structuredClone(currentElem);
            delete elemToPush.subElements;
            currentElem.subElements.push(elemToPush);
            resultMap.set(uniqueKey, currentElem);
          } else {
            const containedElem = resultMap.get(uniqueKey);
            const containedInSubElems = containedElem.subElements.find(
              (elem) => currentElem[uniqueSubElemField$.value] === elem[uniqueSubElemField$.value],
            );

            if (containedInSubElems) {
              colsToAdd.forEach((col) => {
                containedInSubElems[col] = currentElem[col] + containedInSubElems[col];
              });
              containedInSubElems.ctr1 = getCtr(
                containedInSubElems.clicks,
                containedInSubElems.impressions,
              );
              containedInSubElems.eCPM1 = getEcpm(
                containedInSubElems.rawEarning,
                containedInSubElems.impressions,
              );
            } else {
              const elemToPush = structuredClone(currentElem);
              delete elemToPush.subElements;
              containedElem.subElements.push(elemToPush);
            }
            colsToAdd.forEach((col) => {
              containedElem[col] = currentElem[col] + containedElem[col];
            });
            containedElem.ctr1 = getCtr(containedElem.clicks, containedElem.impressions);
            containedElem.eCPM1 = getEcpm(containedElem.rawEarning, containedElem.impressions);
          }
        }
      }
    });

    // sorting
    const sortableItems = Array.from(resultMap.values());

    if (sortConfig !== null) {
      let key = sortConfig.key;
      const direction = sortConfig.direction;

      if (key === 'applications') key = 'apps';

      sortableItems.sort((a, b) => {
        if (key === 'country' && a[key] === '-') return 1;

        if (direction === 'ascending') {
          if (key === 'date') return parseDate(a[key]) - parseDate(b[key]);
          if (typeof a[key] === 'string' && typeof b[key] === 'string') {
            return a[key].localeCompare(b[key]);
          }
          return a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0;
        } else if (direction === 'descending') {
          if (key === 'date') return parseDate(b[key]) - parseDate(a[key]);
          if (typeof a[key] === 'string' && typeof b[key] === 'string') {
            return b[key].localeCompare(a[key]);
          }
          return a[key] > b[key] ? -1 : a[key] < b[key] ? 1 : 0;
        }

        return 0;
      });
    }

    return sortableItems;
  });

  useEffect(() => {
    originalTableData$.value = data?.revenueData?.rows;
  }, [data?.revenueData?.rows]);

  const hideAdvertiserGrouping = data?.hideAdvertiserGrouping;
  const hideInstallsColumn = data?.hideInstallsColumn;

  // reset grouping for account where advertiser is not shown but was preveously selected
  // reset grouping for samsung account if preveously selected
  // it's also connected to isAdmin, but the check happens on server
  useEffect(() => {
    if (hideAdvertiserGrouping && currentRequestType$.value?.startsWith('advertiser')) {
      currentRequestType$.value = 'applications';
      sortConfig$.value = null;
    }
    if (
      isSamsungUser &&
      (currentRequestType$.value !== 'date' || currentRequestType$.value !== 'country')
    ) {
      currentRequestType$.value = 'date';
      sortConfig$.value = null;
    }
  }, [!!hideAdvertiserGrouping, currentRequestType$.value]);

  const numOfRows = sortedFilteredData$.value.length || 0;
  const [visibleRows, setVisibleRows] = useState(
    numOfRows ? Math.min(TABLE_ROW_INITIAL_COUNT, numOfRows) : 0,
  );

  useEffect(() => {
    setVisibleRows(numOfRows ? Math.min(TABLE_ROW_INITIAL_COUNT, numOfRows) : 0);
  }, [numOfRows]);

  const handleLoadMore = () => {
    if (numOfRows === visibleRows) return;
    setVisibleRows((prevVisibleRows) => {
      const newCount = prevVisibleRows + TABLE_ROW_INCREMENT;
      if (newCount > numOfRows) return numOfRows;
      return newCount;
    });
  };

  const handleExport = useCallback(() => {
    const currentGrouping = tableGrouping[currentRequestType$.value]
      ? `_${tableGrouping[currentRequestType$.value]}`
      : '';
    const currentType = currentRequestType$.value + currentGrouping;
    const fileName = `Revenue report ${formatDate(periodFrom)} to ${formatDate(periodTo)}.csv`;
    downloadCSV(sortedFilteredData$.value, fileName, currentType, showAppName);
  }, [periodFrom, periodTo, sortedFilteredData$, currentRequestType$, tableGrouping, showAppName]);

  const renderCols = useMemo(() => {
    return hideInstallsColumn
      ? reportTableColsOrder.filter((col) => col !== 'Installs')
      : reportTableColsOrder;
  }, [!!hideInstallsColumn]);

  return {
    isLoading,
    isFetching,
    originalTableData$,
    isError,
    currentRequestType$,
    sortConfig$,
    sortedFilteredData$,
    visibleRows,
    numOfRows,
    handleLoadMore,
    placementList$,
    countryList$,
    advertiserList$,
    applicationsMap$,
    handleExport,
    hideAdvertiserGrouping,
    hideInstallsColumn,
    renderCols,
    isSamsungUser,
  };
};
